/* global globalThis */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  ChevronDown,
  ChevronRight,
  Database,
  Loader,
} from "lucide-react";
import { filters } from "./filters.js";

const SidebarFilters = ({
  expandedCategories,
  expandedSubcategories,
  selectedFilters,
  selectedMappingDocs,
  setSelectedMappingDocs,
  expandedOptions,
  expandedTypes,
  toggleCategory,
  toggleSubcategory,
  toggleOption,
  toggleType,
  handleFilterSelect,
  renderIcon,
  renderFilterOptions,
  getColorClasses,
}) => {
  const [mappingDocuments, setMappingDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch mapping documents when data-migration subcategory is expanded
  useEffect(() => {
    if (expandedSubcategories["data-migration"]) {
      fetchMappingDocuments();
    }
  }, [expandedSubcategories["data-migration"]]);

  // Listen for corporation changes in localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "selectedCorporation") {
        // Clear existing data and fetch new mapping documents
        setMappingDocuments([]);
        setSelectedMappingDocs([]);

        // Only fetch if data-migration is expanded
        if (expandedSubcategories["data-migration"]) {
          fetchMappingDocuments();
        }
      }
    };

    // Listen for storage events from other tabs/windows
    globalThis.addEventListener("storage", handleStorageChange);

    // For same-tab changes, we need to override localStorage.setItem
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      originalSetItem.apply(this, arguments);

      if (key === "selectedCorporation") {
        // Dispatch a custom event for same-tab detection
        globalThis.dispatchEvent(
          new CustomEvent("localStorageChange", {
            detail: { key, value },
          })
        );
      }
    };

    // Listen for custom events (same tab changes)
    const handleCustomStorageChange = (e) => {
      if (e.detail.key === "selectedCorporation") {
        setMappingDocuments([]);
        setSelectedMappingDocs([]);

        if (expandedSubcategories["data-migration"]) {
          fetchMappingDocuments();
        }
      }
    };

    globalThis.addEventListener("localStorageChange", handleCustomStorageChange);

    // Cleanup
    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
      globalThis.removeEventListener(
        "localStorageChange",
        handleCustomStorageChange
      );
      localStorage.setItem = originalSetItem;
    };
  }, [expandedSubcategories]);

  const fetchMappingDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get selected corporation from localStorage
      const storedCorporation = localStorage.getItem("selectedCorporation");
      let corporationId = 1; // fallback default

      if (storedCorporation) {
        const parsed = JSON.parse(storedCorporation);
        corporationId = parsed.value; // use the value field
      }

      //  Use deployed server API (no tokens required)
      const response = await fetch(
        `https://apicodecheetah.bluegecko.ai:8001/api/mapping-documents?organization_id=${corporationId}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Handle different response structures - ensure we always set an array
      let documents = [];
      if (Array.isArray(data)) {
        documents = data;
      } else if (data && Array.isArray(data.mapping_documents)) {
        documents = data.mapping_documents;
      } else if (data && Array.isArray(data.data)) {
        documents = data.data;
      } else if (data && Array.isArray(data.results)) {
        documents = data.results;
      } else if (data && Array.isArray(data.items)) {
        documents = data.items;
      } else {
        console.warn("Unexpected API response structure:", data);
        documents = [];
      }

      // Map API response fields to UI expected fields
      // API returns: { id, objectDescription }
      // UI expects: { userMappingFileId, uploadedObjDescription }
      const mappedDocuments = documents.map((doc) => ({
        userMappingFileId: doc.id || doc.userMappingFileId,
        uploadedObjDescription:
          doc.objectDescription || doc.uploadedObjDescription,
      }));

      setMappingDocuments(mappedDocuments);
    } catch (err) {
      console.error("Error fetching mapping documents:", err);
      setError("Failed to load mapping documents");
    } finally {
      setLoading(false);
    }
  };

  const handleMappingDocSelect = (docId) => {
    setSelectedMappingDocs((prev) =>
      prev.includes(docId)
        ? prev.filter((id) => id !== docId)
        : [...prev, docId]
    );
  };

  const handleSelectAllMappingDocs = () => {
    const documents = Array.isArray(mappingDocuments) ? mappingDocuments : [];
    if (selectedMappingDocs.length === documents.length) {
      setSelectedMappingDocs([]);
    } else {
      setSelectedMappingDocs(documents.map((doc) => doc.userMappingFileId));
    }
  };

  const renderMappingDocuments = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader className="w-4 h-4 animate-spin mr-2" />
          <span className="text-sm text-gray-500">Loading objects...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="py-2 px-3">
          <span className="text-sm text-red-500">{error}</span>
          <button
            onClick={fetchMappingDocuments}
            className="ml-2 text-xs text-blue-600 hover:underline"
          >
            Retry
          </button>
        </div>
      );
    }

    // Ensure mappingDocuments is always an array
    const documents = Array.isArray(mappingDocuments) ? mappingDocuments : [];

    if (documents.length === 0) {
      return (
        <div className="py-2 px-3">
          <span className="text-sm text-gray-500">
            No mapping documents found
          </span>
        </div>
      );
    }

    return (
      <div className="border border-gray-200 rounded-lg mt-2">
        <div className="p-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-purple-600" />
              <span className="font-medium text-sm text-gray-800">
                Mapping Objects
              </span>
              <span className="text-xs text-gray-500">
                ({documents.length})
              </span>
            </div>
            <button
              onClick={handleSelectAllMappingDocs}
              className="text-xs text-blue-600 hover:underline"
            >
              {selectedMappingDocs.length === documents.length
                ? "Deselect All"
                : "Select All"}
            </button>
          </div>
        </div>

        <div className="max-h-40 overflow-y-auto">
          {documents.map((doc) => (
            <div
              key={doc.userMappingFileId}
              className="flex items-center gap-2 p-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
            >
              <input
                type="checkbox"
                id={`mapping-${doc.userMappingFileId}`}
                checked={selectedMappingDocs.includes(doc.userMappingFileId)}
                onChange={() => handleMappingDocSelect(doc.userMappingFileId)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2"
              />
              <label
                htmlFor={`mapping-${doc.userMappingFileId}`}
                className="flex-1 text-sm text-gray-700 cursor-pointer"
                title={doc.uploadedObjDescription}
              >
                {doc.uploadedObjDescription}
              </label>
            </div>
          ))}
        </div>

        {selectedMappingDocs.length > 0 && (
          <div className="p-2 bg-blue-50 border-t border-blue-200">
            <div className="text-xs text-blue-800">
              <strong>{selectedMappingDocs.length}</strong> object(s) selected
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col">
      <div className="p-2 border-b border-gray-200">
        <div className="flex justify-center items-center gap-2 mb-4">
          <span className="text-gray-800 flex items-center text-xl">
            Business Logic Tracks
          </span>
        </div>

        {selectedFilters.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Selected Filters:
            </h3>
            <div className="flex flex-wrap gap-2">
              {selectedFilters.map((filterId) => (
                <button
                  key={filterId}
                  type="button"
                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full cursor-pointer"
                  onClick={() => handleFilterSelect(filterId)}
                  onKeyDown={(e) => e.key === "Enter" && handleFilterSelect(filterId)}
                >
                  {filterId} ×
                </button>
              ))}
            </div>
          </div>
        )}
        {selectedMappingDocs.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Selected Objects:
            </h3>
            <div className="flex flex-wrap gap-2 max-h-20 overflow-y-auto">
              {selectedMappingDocs.map((docId) => {
                const doc = mappingDocuments.find(
                  (d) => d.userMappingFileId === docId
                );
                return (
                  <button
                    key={docId}
                    type="button"
                    className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full cursor-pointer"
                    onClick={() => handleMappingDocSelect(docId)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleMappingDocSelect(docId)
                    }
                    title={doc?.uploadedObjDescription}
                  >
                    {doc?.uploadedObjDescription?.substring(0, 15)}... ×
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {Object.entries(filters).map(([categoryId, category]) => (
          <div key={categoryId} className="border border-gray-200 rounded-lg">
            <div
              role="button"
              tabIndex={0}
              className={`p-3 cursor-pointer flex items-center gap-2 ${getColorClasses(
                category.color
              )} rounded-t-lg`}
              onClick={() => toggleCategory(categoryId)}
              onKeyDown={(e) =>
                e.key === "Enter" && toggleCategory(categoryId)
              }
            >
              {expandedCategories[categoryId] ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              {renderIcon(category.icon)}
              <span className="font-medium text-sm">{category.label}</span>
            </div>

            {expandedCategories[categoryId] && (
              <div className="p-2 space-y-2">
                {Object.entries(category.subcategories).map(
                  ([subcategoryId, subcategory]) => (
                    <div
                      key={subcategoryId}
                      className="border border-gray-100 rounded"
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        className="p-2 cursor-pointer flex items-center gap-2 hover:bg-gray-50"
                        onClick={() => toggleSubcategory(subcategoryId)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && toggleSubcategory(subcategoryId)
                        }
                      >
                        {expandedSubcategories[subcategoryId] ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                        {renderIcon(subcategory.icon, "w-3 h-3")}
                        <span className="text-sm">{subcategory.label}</span>
                      </div>

                      {expandedSubcategories[subcategoryId] && (
                        <div className="pl-2 pb-2">
                          {/* Show mapping documents FIRST for data-migration */}
                          {subcategoryId === "data-migration" &&
                            renderMappingDocuments()}

                          {/* Then show filter options */}
                          {subcategory.options &&
                            renderFilterOptions(subcategory.options)}
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

SidebarFilters.propTypes = {
  expandedCategories: PropTypes.objectOf(PropTypes.bool).isRequired,
  expandedSubcategories: PropTypes.objectOf(PropTypes.bool).isRequired,
  selectedFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedMappingDocs: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  setSelectedMappingDocs: PropTypes.func.isRequired,
  expandedOptions: PropTypes.objectOf(PropTypes.bool).isRequired,
  expandedTypes: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleCategory: PropTypes.func.isRequired,
  toggleSubcategory: PropTypes.func.isRequired,
  toggleOption: PropTypes.func.isRequired,
  toggleType: PropTypes.func.isRequired,
  handleFilterSelect: PropTypes.func.isRequired,
  renderIcon: PropTypes.func.isRequired,
  renderFilterOptions: PropTypes.func.isRequired,
  getColorClasses: PropTypes.func.isRequired,
};

export default SidebarFilters;